import { useMemo } from "react";
import { array, object, string } from "yup";
import {
  AllowedBlockchains,
  ProductionBlockchains,
} from "../../../../store/chain/models/internal";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { keys, omitBy, reduceObject } from "../../../../utils/utils";
import { FormType as ParentFormType } from "../common/form";

export type FormType = Partial<
  Record<
    AllowedBlockchains,
    {
      pledgingInfo: Array<
        {
          currency: string;
        } & LaunchConfigurationsDTO.PledgingInfo.T
      >;
    }
  >
>;

export const useFormSchema = () =>
  useMemo(
    () =>
      object().shape(
        ProductionBlockchains.values.reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: object().shape({
              pledgingInfo: array()
                .of(
                  object()
                    .shape({
                      currency: string().required("Field is required"),
                    })
                    .concat(LaunchConfigurationsDTO.PledgingInfo.schema)
                    .required()
                )
                .min(1, "You must have at least 1 currency"),
            }),
          }),
          {}
        )
      ),
    []
  );

/**
 * Transform array-like crypto chains to object-like. The array-like structure is used to
 * cater for the form whereas the object-like is used for the actual DTO.
 */
export function fromArrayLike(
  data: FormType
): ParentFormType["configuration"]["cryptoChains"] {
  const omitted: typeof data = omitBy(
    data,
    (x) => x.pledgingInfo === undefined
  );

  return reduceObject(
    omitted,
    (acc, val, key) => ({
      ...acc,
      [key]: {
        pledgingInfo: val.pledgingInfo.reduce(
          (acc2, { currency, ...curr2 }) => ({
            ...acc2,
            [currency]: curr2,
          }),
          {}
        ),
      },
    }),
    {}
  );
}

export function toArrayLike(
  data: ParentFormType["configuration"]["cryptoChains"]
): FormType {
  if (data === null || data === undefined) return {};
  return reduceObject(
    data,
    (acc, val, key) => ({
      ...acc,
      [key]: {
        pledgingInfo: keys(val.pledgingInfo).map((currency) => ({
          currency,
          ...val.pledgingInfo[currency],
        })),
      },
    }),
    {}
  );
}
